/**
 * @file app/paymentModules/npipe/npipeHttp.js
 *
 * Platform-neutral helpers shared by the NPIPE transports: HMAC signing, the
 * transport error type, UTF-8 coding without TextEncoder/TextDecoder (not
 * guaranteed on Hermes) and a minimal incremental HTTP/1.1 response parser
 * (Content-Length, chunked and read-until-close bodies).
 */

import CryptoJS from 'crypto-js';

/** Transport failure with a machine readable `code`. */
export class NpipeTransportError extends Error {
    /**
     * @param {'TIMEOUT'|'UNREACHABLE'|'TLS'|'BAD_RESPONSE'} code failure class
     * @param {string} [message] human readable detail
     */
    constructor(code, message) {
        super(message || code);
        this.name = 'NpipeTransportError';
        this.code = code;
    }
}

/**
 * Base64(HMAC-SHA256(rawBody, secret)) as required for the X-Request-Hash header.
 * crypto-js is pure JS, so it behaves the same under Hermes, Node and the web build.
 * @param {string} rawBody exact request body that will be sent
 * @param {string} secret shared HMAC secret
 * @returns {string} Base64 digest
 */
export function signBody(rawBody, secret) {
    return CryptoJS.enc.Base64.stringify(CryptoJS.HmacSHA256(String(rawBody), String(secret)));
}

/**
 * Encode a JS string as UTF-8 bytes.
 * @param {string} str text
 * @returns {Uint8Array} bytes
 */
export function utf8Encode(str) {
    const out = [];
    const s = String(str);
    for (let i = 0; i < s.length; i++) {
        let c = s.charCodeAt(i);
        if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
            const d = s.charCodeAt(i + 1);
            if (d >= 0xdc00 && d <= 0xdfff) {
                c = 0x10000 + ((c - 0xd800) << 10) + (d - 0xdc00);
                i++;
            }
        }
        if (c < 0x80) {
            out.push(c);
        } else if (c < 0x800) {
            out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
        } else if (c < 0x10000) {
            out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
        } else {
            out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
        }
    }
    return Uint8Array.from(out);
}

/**
 * Decode UTF-8 bytes; invalid sequences become U+FFFD.
 * @param {Uint8Array} bytes data
 * @returns {string} text
 */
export function utf8Decode(bytes) {
    const parts = [];
    let chunk = [];
    const flush = () => {
        if (chunk.length) {
            parts.push(String.fromCharCode.apply(null, chunk));
            chunk = [];
        }
    };
    const push = (cp) => {
        if (cp > 0xffff) {
            const v = cp - 0x10000;
            chunk.push(0xd800 + (v >> 10), 0xdc00 + (v & 0x3ff));
        } else {
            chunk.push(cp);
        }
        if (chunk.length > 4096) {
            flush();
        }
    };
    let i = 0;
    while (i < bytes.length) {
        const b = bytes[i];
        let need = 0;
        let cp = 0;
        let min = 0;
        if (b < 0x80) {
            push(b);
            i++;
            continue;
        } else if (b >= 0xc2 && b < 0xe0) {
            need = 1;
            cp = b & 0x1f;
            min = 0x80;
        } else if (b >= 0xe0 && b < 0xf0) {
            need = 2;
            cp = b & 0x0f;
            min = 0x800;
        } else if (b >= 0xf0 && b < 0xf5) {
            need = 3;
            cp = b & 0x07;
            min = 0x10000;
        } else {
            push(0xfffd);
            i++;
            continue;
        }
        let ok = true;
        let j = 1;
        for (; j <= need; j++) {
            const nb = bytes[i + j];
            if (nb === undefined || (nb & 0xc0) !== 0x80) {
                ok = false;
                break;
            }
            cp = (cp << 6) | (nb & 0x3f);
        }
        if (ok && cp >= min && cp <= 0x10ffff && !(cp >= 0xd800 && cp <= 0xdfff)) {
            push(cp);
            i += need + 1;
        } else {
            push(0xfffd);
            i += ok ? need + 1 : j;
        }
    }
    flush();
    return parts.join('');
}

/**
 * Build the raw HTTP/1.1 POST request bytes.
 * @param {{ host:string, port:number|string, path?:string, headers?:Object, rawBody:string }} p request
 * @returns {Uint8Array} head + body bytes
 */
export function buildHttpRequest({ host, port, path = '/perform_action', headers = {}, rawBody }) {
    const body = utf8Encode(rawBody);
    const lines = [
        `POST ${path} HTTP/1.1`,
        `Host: ${host}:${port}`,
        'Accept: application/json',
        'Connection: close',
        `Content-Length: ${body.length}`,
    ];
    Object.keys(headers).forEach((k) => lines.push(`${k}: ${headers[k]}`));
    const head = utf8Encode(`${lines.join('\r\n')}\r\n\r\n`);
    const out = new Uint8Array(head.length + body.length);
    out.set(head, 0);
    out.set(body, head.length);
    return out;
}

const concat = (a, b) => {
    if (!a.length) {
        return b;
    }
    const out = new Uint8Array(a.length + b.length);
    out.set(a, 0);
    out.set(b, a.length);
    return out;
};

const indexOfCrlfCrlf = (buf) => {
    for (let i = 0; i + 3 < buf.length; i++) {
        if (buf[i] === 13 && buf[i + 1] === 10 && buf[i + 2] === 13 && buf[i + 3] === 10) {
            return i;
        }
    }
    return -1;
};

const indexOfCrlf = (buf, from) => {
    for (let i = from; i + 1 < buf.length; i++) {
        if (buf[i] === 13 && buf[i + 1] === 10) {
            return i;
        }
    }
    return -1;
};

/** Decode a chunked body; returns { complete, data }. Throws BAD_RESPONSE when malformed. */
const decodeChunked = (buf) => {
    const pieces = [];
    let pos = 0;
    for (;;) {
        const eol = indexOfCrlf(buf, pos);
        if (eol < 0) {
            return { complete: false };
        }
        const sizeText = utf8Decode(buf.subarray(pos, eol)).split(';')[0].trim();
        if (!/^[0-9a-fA-F]+$/.test(sizeText)) {
            throw new NpipeTransportError('BAD_RESPONSE', 'Invalid chunk size');
        }
        const size = parseInt(sizeText, 16);
        pos = eol + 2;
        if (size === 0) {
            // trailers are ignored; the terminating blank line must have arrived
            return indexOfCrlf(buf, pos) >= 0 ? { complete: true, data: concatAll(pieces) } : { complete: false };
        }
        if (buf.length < pos + size + 2) {
            return { complete: false };
        }
        pieces.push(buf.subarray(pos, pos + size));
        pos += size + 2;
    }
};

const concatAll = (pieces) => pieces.reduce((acc, p) => concat(acc, p), new Uint8Array(0));

/**
 * Incremental HTTP/1.1 response parser.
 * @returns {{ push:(chunk:Uint8Array)=>void, end:()=>void, isComplete:()=>boolean, result:()=>{status:number, headers:Object, bodyText:string} }} parser
 */
export function createHttpResponseParser() {
    let buf = new Uint8Array(0);
    let head = null;
    let bodyBytes = null;
    let closed = false;

    const parseHead = () => {
        const end = indexOfCrlfCrlf(buf);
        if (end < 0) {
            return;
        }
        const lines = utf8Decode(buf.subarray(0, end)).split('\r\n');
        const m = /^HTTP\/\d(?:\.\d)?\s+(\d{3})/.exec(lines[0] || '');
        if (!m) {
            throw new NpipeTransportError('BAD_RESPONSE', 'Invalid HTTP status line');
        }
        const headers = {};
        for (let i = 1; i < lines.length; i++) {
            const idx = lines[i].indexOf(':');
            if (idx > 0) {
                headers[lines[i].slice(0, idx).trim().toLowerCase()] = lines[i].slice(idx + 1).trim();
            }
        }
        head = { status: Number(m[1]), headers, bodyStart: end + 4 };
    };

    const evaluate = () => {
        if (!head) {
            parseHead();
        }
        if (!head || bodyBytes) {
            return;
        }
        const body = buf.subarray(head.bodyStart);
        const { status, headers } = head;
        if (status === 204 || status === 304 || (status >= 100 && status < 200)) {
            bodyBytes = new Uint8Array(0);
        } else if (/chunked/i.test(headers['transfer-encoding'] || '')) {
            const r = decodeChunked(body);
            if (r.complete) {
                bodyBytes = r.data;
            }
        } else if (headers['content-length'] !== undefined) {
            const len = parseInt(headers['content-length'], 10);
            if (!Number.isFinite(len) || len < 0) {
                throw new NpipeTransportError('BAD_RESPONSE', 'Invalid Content-Length');
            }
            if (body.length >= len) {
                bodyBytes = body.subarray(0, len);
            }
        } else if (closed) {
            bodyBytes = body;
        }
    };

    return {
        push(chunk) {
            buf = concat(buf, chunk);
            evaluate();
        },
        end() {
            closed = true;
            evaluate();
        },
        isComplete() {
            return Boolean(bodyBytes);
        },
        result() {
            if (!head) {
                throw new NpipeTransportError('BAD_RESPONSE', 'Connection closed before a response was received');
            }
            if (!bodyBytes) {
                throw new NpipeTransportError('BAD_RESPONSE', 'Connection closed before the response body was complete');
            }
            return { status: head.status, headers: head.headers, bodyText: utf8Decode(bodyBytes) };
        },
    };
}
